import { IFaireMoney } from '@faire/web-api--source/indigofair/data/IFaireMoney';
import { IImage } from '@faire/web-api--source/indigofair/data/IImage';
import { IItemPromoPrice } from '@faire/web-api--source/indigofair/data/IItemPromoPrice';
import { IProductComputedFields } from '@faire/web-api--source/indigofair/data/IProductComputedFields';
import { IProductCustomization } from '@faire/web-api--source/indigofair/data/IProductCustomization';
import { IProductInformation } from '@faire/web-api--source/indigofair/data/IProductInformation';
import { IProductOption } from '@faire/web-api--source/indigofair/data/IProductOption';
import { IProductOrderabilityCounts } from '@faire/web-api--source/indigofair/data/IProductOrderabilityCounts';
import { IProductTaxonomyAttributeValues } from '@faire/web-api--source/indigofair/data/IProductTaxonomyAttributeValues';
import { IProductTesterPrice } from '@faire/web-api--source/indigofair/data/IProductTesterPrice';
import { IProductVariation } from '@faire/web-api--source/indigofair/data/IProductVariation';
import { IProductVariationMap } from '@faire/web-api--source/indigofair/data/IProductVariationMap';
import { ITaxonomyType } from '@faire/web-api--source/indigofair/data/ITaxonomyType';
import { ITranslationOverride } from '@faire/web-api--source/indigofair/data/ITranslationOverride';

export namespace IProduct {
  export enum NonReturnableReason {
    /**
     * @deprecated
     */
    FOOD = 'FOOD',
    HOLIDAY = 'HOLIDAY',
    /**
     * @deprecated
     */
    MIX_AND_MATCH = 'MIX_AND_MATCH',
    OVER_RETAIL_PRICE_LIMIT = 'OVER_RETAIL_PRICE_LIMIT',
    FACE_MASK = 'FACE_MASK',
    /**
     * @deprecated
     */
    IS_SAMPLE = 'IS_SAMPLE',
    /**
     * used as a default for order items with no product based non-returnable reason
     */
    UNKNOWN = 'UNKNOWN',
    NOT_RETURNABLE_FOR_BRAND = 'NOT_RETURNABLE_FOR_BRAND',
    DISPLAY_PRODUCT = 'DISPLAY_PRODUCT',
    CBD = 'CBD',
  }

  export namespace INonReturnableReasonData {
    /**
     * Return an instance of INonReturnableReasonData with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<INonReturnableReasonData>
    ): INonReturnableReasonData =>
      ({
        ...(partial ?? {}),
      }) as INonReturnableReasonData;
  }

  export interface INonReturnableReasonData {
    non_returnable_reason:
      | keyof typeof IProduct.NonReturnableReason
      | undefined;
    default_message: string | undefined;
    short_message: string | undefined;
  }

  export enum NonResellableReason {
    FOOD_DRINK = 'FOOD_DRINK',
  }

  export enum RelationshipType {
    /**
     * source is what the sample is of
     */
    SAMPLE = 'SAMPLE',
  }

  /**
   * Sale States for Products and Options
   */
  export enum State {
    /**
     * Is the product deleted
     * Replaced with LifecycleState.SOFT_DELETED
     * @deprecated
     */
    DELETED = 'DELETED',
    /**
     * Is the product intended to be sold on Faire
     */
    FOR_SALE = 'FOR_SALE',
    /**
     * Is the product intended to be sold on Faire, but unable to be for some reason.
     * Eg: Out of stock, on Backorder
     */
    SALES_PAUSED = 'SALES_PAUSED',
    /**
     * Is the Product not able to be sold on Faire for some reason.
     * Example reasons:
     *  - the Product is in LifecycleState.UNPUBLISHED or LifecycleState.DRAFT
     *  - the Product has pricing violations and is not sellable
     */
    NOT_FOR_SALE = 'NOT_FOR_SALE',
  }

  export enum LifecycleState {
    /**
     * Draft products are only visible to brands, and are not validated
     */
    DRAFT = 'DRAFT',
    /**
     * Published products are visible to everyone
     */
    PUBLISHED = 'PUBLISHED',
    /**
     * Unpublished products are only visible to brands
     */
    UNPUBLISHED = 'UNPUBLISHED',
    /**
     * Soft deleted products are only visible to brands via special checks
     */
    SOFT_DELETED = 'SOFT_DELETED',
  }

  /**
   * Shown only to brands, represents inventory state of a product or product option
   */
  export enum BrandAvailability {
    BRAND_AVAILABILITY_UNKNOWN = 'BRAND_AVAILABILITY_UNKNOWN',
    IN_STOCK = 'IN_STOCK',
    OUT_OF_STOCK = 'OUT_OF_STOCK',
  }

  export enum ProductOffering {
    /**
     * Normal products are visible in search and in recommendations
     */
    NORMAL_PRODUCT = 'NORMAL_PRODUCT',
    /**
     * Sample products are no longer being used
     * @deprecated
     */
    SAMPLE_PRODUCT = 'SAMPLE_PRODUCT',
  }

  export enum MerchandisingType {
    MERCHANDISING_TYPE_UNKNOWN = 'MERCHANDISING_TYPE_UNKNOWN',
    REGULAR = 'REGULAR',
    DISPLAY = 'DISPLAY',
    CUSTOM = 'CUSTOM',
  }

  /**
   * This enum should be passed in for TranslationOverride#fieldName so that the backend can determine what field
   * on the database entity should be overridden by the relevant translation
   */
  export enum TranslatableField {
    TRANSLATABLE_FIELD_UNKNOWN = 'TRANSLATABLE_FIELD_UNKNOWN',
    NAME = 'NAME',
    DESCRIPTION = 'DESCRIPTION',
    SHORT_DESCRIPTION = 'SHORT_DESCRIPTION',
  }

  /**
   * The method by which the brand has selected to sell this product
   */
  export enum SellingMethod {
    /**
     * See unitMultiplier for details
     */
    CASE = 'CASE',
    /**
     * See Type.PREPACK for details
     */
    PREPACK = 'PREPACK',
    /**
     * See perStyleMinOrderQuantity for details
     */
    PER_STYLE_MOQ = 'PER_STYLE_MOQ',
  }

  export enum CreatedBy {
    CREATED_BY_UNKNOWN = 'CREATED_BY_UNKNOWN',
    /**
     * Created by Faire employee
     */
    FAIRE = 'FAIRE',
    /**
     * Created by a Business Process Optimization
     */
    BPO = 'BPO',
    /**
     * Created by the brand
     */
    BRAND = 'BRAND',
    /**
     * Created by the retailer (e.g. for a purchase order)
     */
    RETAILER = 'RETAILER',
  }

  export enum CreatedWith {
    CREATED_WITH_UNKNOWN = 'CREATED_WITH_UNKNOWN',
    /**
     * Brand portal single product upload
     * https://cdn.faire.com/fastly/7779ae2cadfa445c751f8284b1bce62a60aecbc5617678d3f8f7e8ef49551a59.png
     */
    BRAND_PORTAL_SINGLE_PRODUCT = 'BRAND_PORTAL_SINGLE_PRODUCT',
    /**
     * Brand portal bulk product upload
     * https://cdn.faire.com/fastly/b90b75af2e18c4e756f0acebf21f3228c74c7116c36b13bbb3392ddf3a7cb78e.png
     */
    BRAND_PORTAL_MULTIPLE_PRODUCTS = 'BRAND_PORTAL_MULTIPLE_PRODUCTS',
    /**
     * Brand portal shopify import
     * https://cdn.faire.com/fastly/12f8c45fc6d1b97d6c5bfc809507b9f448106cd6b14e80f886a95d3f6c33805b.png
     */
    BRAND_PORTAL_IMPORT_FROM_SHOPIFY = 'BRAND_PORTAL_IMPORT_FROM_SHOPIFY',
    /**
     * Imported with admin tool
     * https://cdn.faire.com/fastly/13c94993f530a1673d4e4740f4fa8593c5b49559a77b2ce87733db18986b6f11.png
     */
    ADMIN_BULK_IMPORT_CSV = 'ADMIN_BULK_IMPORT_CSV',
    /**
     * Created with a purchase order tool
     * https://cdn.faire.com/fastly/503d24197583bbb993edcfa2d93f5a4377237c9717efd75a02452ecadcc52954.png
     */
    PURCHASE_ORDER = 'PURCHASE_ORDER',
    /**
     * Created for a brand invoice
     */
    BRAND_INVOICE = 'BRAND_INVOICE',
    /**
     * Created with external API
     */
    EXTERNAL_API = 'EXTERNAL_API',
    /**
     * Created with external API
     * https://faire.github.io/external-api-docs/#create-a-product
     * https://faire.github.io/external-api-docs/#create-a-batch-of-products
     */
    EXTERNAL_API_V2 = 'EXTERNAL_API_V2',
    /**
     * Created for free display attached to a product based collection
     * https://cdn.faire.com/fastly/62e94bd20f3d6706d9e0e4fc819612ff20728f1b3f6c46305d275eb3932c5bdf.jpeg
     */
    BRAND_PORTAL_FREE_MERCHANDISING_DISPLAY = 'BRAND_PORTAL_FREE_MERCHANDISING_DISPLAY',
    /**
     * Fields below are for extension to product update sources
     * Created with single product editor in brand portal
     */
    SINGLE_PRODUCT_EDITOR = 'SINGLE_PRODUCT_EDITOR',
    /**
     * Created with bulk product editor in brand portal
     */
    BULK_PRODUCT_EDITOR = 'BULK_PRODUCT_EDITOR',
    /**
     * Created with bulk product editor in listing quality focused mode
     */
    LISTING_QUALITY_BULK_EDITOR = 'LISTING_QUALITY_BULK_EDITOR',
    /**
     * Catch-all for admin actions to update products
     */
    ADMIN = 'ADMIN',
    /**
     * Modified through an AI recommendation
     * https://www.notion.so/faire/Product-Title-Description-Recommendations-81d23044b8bf4ef787b79fb3a36f17e9
     */
    CONTENT_RECOMMENDATION = 'CONTENT_RECOMMENDATION',
    /**
     * Custom products created by accepted unlisted product requests
     * https://www.notion.so/faire/Request-Unlisted-Products-59750f7f051744c382d4c7b4c585203a?pvs=4
     */
    UNLISTED_PRODUCTS = 'UNLISTED_PRODUCTS',
    /**
     * When a Brand accepts a pricing parity reference is applied.
     */
    PRICING_PARITY_REFERENCE_PRICES_APPLIED = 'PRICING_PARITY_REFERENCE_PRICES_APPLIED',
    /**
     * Created by a new brand through the web bridge scraper during the onboarding flow
     * https://www.notion.so/faire/Web-Bridge-1742efb5c25a80638572f385209601b3
     */
    WEB_BRIDGE_ONBOARDING = 'WEB_BRIDGE_ONBOARDING',
  }

  export namespace IOptionTokenList {
    /**
     * Return an instance of IOptionTokenList with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IOptionTokenList>
    ): IOptionTokenList =>
      ({
        option_tokens: [],
        style_variations: [],
        ...(partial ?? {}),
      }) as IOptionTokenList;
  }

  export interface IOptionTokenList {
    /**
     * Ordered list of option tokens based on the size value's index in the variation map.
     * @protobufOption (.faire.common.diffAs) = ORDERED
     */
    option_tokens: Array<string>;
    selling_method: keyof typeof IProduct.SellingMethod | undefined;
    /**
     * The product variations that make up the style represented by the key in styleGroups pointing to this object.
     * Ordered by the variation maps entry name ordering.
     * @protobufOption (.faire.common.diffAs) = ORDERED
     */
    style_variations: Array<IProductVariation>;
  }

  export enum PromotedType {
    PROMOTED_TYPE_UNKNOWN = 'PROMOTED_TYPE_UNKNOWN',
    FAIRE_DIRECT_REWARDS = 'FAIRE_DIRECT_REWARDS',
    NEW_BRANDS = 'NEW_BRANDS',
    APPAREL_PERSONALIZED_PRICE_POINT = 'APPAREL_PERSONALIZED_PRICE_POINT',
    INSIDER = 'INSIDER',
    ADS = 'ADS',
  }

  export enum BreakDownPrepacksState {
    BREAK_DOWN_PREPACKS_STATE_UNKNOWN = 'BREAK_DOWN_PREPACKS_STATE_UNKNOWN',
    ON = 'ON',
    OFF = 'OFF',
    SMART_RECOGNITION_POSITIVE = 'SMART_RECOGNITION_POSITIVE',
    SMART_RECOGNITION_NEGATIVE = 'SMART_RECOGNITION_NEGATIVE',
  }

  /**
   * Return an instance of IProduct with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IProduct>): IProduct =>
    ({
      tester_prices: [],
      options: [],
      images: [],
      customizations: [],
      board_tokens: [],
      product_information: [],
      attribute_values: [],
      translation_overrides: [],
      style_groups: {},
      promoted_by: [],
      ...(partial ?? {}),
    }) as IProduct;
}

/**
 * @protobufOption (enableProductModelFiltering) = true
 */
export interface IProduct {
  /**
   * @protobufOption (.faire.common.tokenType) = PRODUCT
   * @protobufOption (.faire.wire_cache.identifier) = true
   */
  token: string | undefined;
  /**
   * To be used only internal by the server, not for external APIs.
   * @protobufOption (.faire.common.diffIgnore) = ALWAYS
   * @protobufOption (.indigofair.data.redacted) = true
   */
  id: number | undefined;
  /**
   * We expose the version for caching purposes.
   * @protobufOption (.faire.common.diffIgnore) = ALWAYS
   */
  version: number | undefined;
  /**
   * @protobufOption (.faire.common.tokenType) = BRAND
   */
  brand_token: string | undefined;
  /**
   * If the brandToken was overwritten because of Maker Mix and Match,
   * this keeps the original token. In all other cases, it's just a copy of the brand token.
   */
  original_brand_token: string | undefined;
  /**
   * Same as originalBrandToken but for a brand's name.
   */
  original_brand_name: string | undefined;
  /**
   * @protobufOption (.faire.wire_cache.isLocalized) = true
   */
  name: string | undefined;
  /**
   * @deprecated
   */
  untranslated_name: string | undefined;
  /**
   * @protobufOption (.faire.wire_cache.isLocalized) = true
   */
  short_description: string | undefined;
  /**
   * @protobufOption (.faire.wire_cache.isLocalized) = true
   */
  description: string | undefined;
  machine_translated_english_name: string | undefined;
  machine_translated_english_description: string | undefined;
  /**
   * READ-ONLY
   * Maximum price of all the options.
   * Use the maxOptionWholesalePrice instead (which uses FaireMoney type)
   * @deprecated
   */
  wholesale_price_cents: number | undefined;
  /**
   * READ-ONLY
   * Maximum price of all the options.
   * Use the the maxOptionRetailPrice instead (which uses FaireMoney type)
   * @deprecated
   */
  retail_price_cents: number | undefined;
  /**
   * @deprecated
   */
  tester_price_cents: number | undefined;
  /**
   * This is deprecated in favor of `testerPrices`.
   * @deprecated
   */
  tester_price: IFaireMoney | undefined;
  /**
   * If this product offers testers, this is the price of each tester (regardless of option) by currency.
   */
  tester_prices: Array<IProductTesterPrice>;
  /**
   * WIRE-FILTER GENERATED: computed tester promo price
   */
  tester_promo_price: IItemPromoPrice | undefined;
  /**
   * Also known as case size or case quantity. This is the unit size that this product ships in.
   */
  unit_multiplier: number | undefined;
  /**
   * An unordered list of all non-deleted options for a Product.
   * @protobufOption (.faire.common.diffAs) = UNORDERED
   * @protobufOption (.faire.common.uniqueKey) = "token"
   * @protobufOption (.faire.wire_cache.isLocalized) = true
   */
  options: Array<IProductOption>;
  /**
   * An ordered list of images for display.
   * @protobufOption (.faire.common.diffAs) = ORDERED
   * @protobufOption (.faire.common.uniqueKey) = "token"
   */
  images: Array<IImage>;
  /**
   * The old score we used for globally ranking products. Replaced by newScore.
   * @deprecated
   * @protobufOption (.indigofair.data.redacted) = true
   */
  score: number | undefined;
  /**
   * A score we use for globally ranking products.
   * @protobufOption (.indigofair.data.redacted) = true
   */
  new_score: number | undefined;
  /**
   * This product was tagged as a best seller for its maker. This is not a global best seller flag,
   * just local to the maker.
   */
  maker_best_seller: boolean | undefined;
  /**
   * DEPRECATED Use DbProductRelativePosition and its related APIs instead
   * @deprecated
   * @protobufOption (.faire.common.diffIgnore) = ALWAYS
   */
  maker_relative_position: number | undefined;
  /**
   * When the earliest option should become available for sale again.
   */
  backordered_until_date: number | undefined;
  /**
   * Whether the product is "new" for some reasonable definition of new to mean "newly added".
   */
  is_new: boolean | undefined;
  /**
   * Whether or not the product is a custom one-off product. Use merchandisingType == CUSTOM instead
   * @deprecated
   */
  custom: boolean | undefined;
  idempotence_token: string | undefined;
  is_high_sell_through: boolean | undefined;
  non_returnable_reason: keyof typeof IProduct.NonReturnableReason | undefined;
  non_returnable_reason_data: IProduct.INonReturnableReasonData | undefined;
  non_resellable_reason: keyof typeof IProduct.NonResellableReason | undefined;
  recent_order_count: number | undefined;
  recent_ordered_quantity_count: number | undefined;
  /**
   * The ISO Alpha-3 country code where this product was produced.
   */
  made_in_country: string | undefined;
  /**
   * Original version of MOQ. A retailer must order >= MOQ of each product option they select.
   * Also used to manage inventory and determine when an option should no longer be sold
   */
  min_order_quantity: number | undefined;
  /**
   * Open sizing version of MOQ. A retailer can meet MOQ by combining multiple product options that are
   * different sizes of the same style. This only makes sense on multi-variant products that have a "Size" variation.
   */
  per_style_min_order_quantity: number | undefined;
  customizations: Array<IProductCustomization>;
  /**
   * @protobufOption (.faire.wire_cache.isLocalized) = true
   */
  variation_map: IProductVariationMap | undefined;
  /**
   * Whether this product is currently pre-orderable. This value is true if any of the options are preorderable.
   */
  preorderable: boolean | undefined;
  /**
   * Shipping window for this pre-order product.
   * This is the start (first day) of the window.
   * The window lasts for expectedShipWindowDays days.
   * This value is computed from the earliest expected ship date of all preorderable options.
   */
  expected_ship_date: number | undefined;
  /**
   * Shipping window durations for this pre-order product.
   * This value is computed from the start of the earliest shipping window through the end of the
   * latest shipping window of all preorderable options.
   */
  expected_ship_window_days: number | undefined;
  /**
   * The last date this product can be ordered / active on Faire
   * This value is computed from the earliest stopSellingAt of all preorderable options.
   */
  order_by_date: number | undefined;
  /**
   * Keep products active past their orderByDate.
   * Products are traditionally auto deactivated at order date (or start ship date as fallback). Setting
   * this flag to true avoids this auto deactivation and requires brand to explicitly deactivate the product.
   * When a product reaches the order by date and this flag is true, the order remains active and it can still be
   * ordered: preordered before start ship date, and "immediately" ordered after the start ship date.
   */
  keep_active_past_order_by_date: boolean | undefined;
  /**
   * This flag is only populated on specific endpoints like /api/search/maker-products
   * when there are "fake product options" (that represent prepacks) in the response
   * Check that it is correctly populated before using it.
   * If you want to check if any product options are sold in prepacks,
   * you may actually want to check options.any { !it.notForIndividualSale }
   */
  has_pre_packs_available: boolean | undefined;
  /**
   * eventual replacement field for active & deleted
   */
  state: keyof typeof IProduct.State | undefined;
  /**
   * the product lifecycle state
   */
  lifecycle_state: keyof typeof IProduct.LifecycleState | undefined;
  /**
   * Shown to brands to represent inventory state
   */
  brand_availability: keyof typeof IProduct.BrandAvailability | undefined;
  /**
   * determines if the state of the options can be SALES_PAUSED for stock reasons
   */
  allow_sales_when_out_of_stock: boolean | undefined;
  taxonomy_type: ITaxonomyType | undefined;
  created_at: number | undefined;
  /**
   * @protobufOption (.faire.common.diffIgnore) = ALWAYS
   * @protobufOption (.indigofair.data.redacted) = true
   */
  updated_at: number | undefined;
  /**
   * Tokens of boards where the current retailer has saved this product
   */
  board_tokens: Array<string>;
  /**
   * List of the product information fields associated with this product. Not filtered by category
   * @deprecated
   * @protobufOption (.faire.common.diffIgnore) = ALWAYS
   */
  product_information: Array<IProductInformation>;
  /**
   * How the product is offered
   * @deprecated
   */
  product_offering: keyof typeof IProduct.ProductOffering | undefined;
  first_purchasable_at: number | undefined;
  /**
   * @protobufOption (.faire.common.diffAs) = UNORDERED
   * @protobufOption (.faire.common.uniqueKey) = "attribute.slug"
   */
  attribute_values: Array<IProductTaxonomyAttributeValues>;
  is_apparel: boolean | undefined;
  parent_product_token: string | undefined;
  translation_overrides: Array<ITranslationOverride>;
  /**
   * Groups ProductOption tokens by "style," which is defined as the non-size variations of a ProductOption
   * If this product does not have a "Size" variation, the map will be empty.
   * Within each style, the option tokens are sorted by size based on the order of the size values in the variation map.
   * This grouping is done on backend for the convenience of clients that all require the same logic and is relevant
   * to products sold in open sizing and prepacks.
   */
  style_groups: Partial<Record<string, IProduct.IOptionTokenList>>;
  /**
   * maxOptionWholesalePrice and maxOptionRetailPrice are replacing wholesale_price_cents and retail_price_cents,
   * as we are moving away to using FaireMoney type for all the money fields. Please note that we shouldn't expect these to be populated
   * in web-brand.
   */
  max_option_wholesale_price: IFaireMoney | undefined;
  max_option_retail_price: IFaireMoney | undefined;
  created_with: keyof typeof IProduct.CreatedWith | undefined;
  original_locale_id: string | undefined;
  is_highlighted_product_for_promotional_event: boolean | undefined;
  is_promoted: boolean | undefined;
  /**
   * This field is populated by search and not backed by the product table. This is currently used to determine whether
   * or not the product that was clicked on was promoted, but could be extended in the future.
   */
  promoted_by: Array<keyof typeof IProduct.PromotedType>;
  /**
   * @deprecated
   */
  product_computed_fields: IProductComputedFields | undefined;
  product_orderability_counts: IProductOrderabilityCounts | undefined;
  has_mixed_ship_dates: boolean | undefined;
  is_fallback: boolean | undefined;
  break_down_prepacks_state:
    | keyof typeof IProduct.BreakDownPrepacksState
    | undefined;
  smart_recognition_banner_dismissed: boolean | undefined;
  merchandising_type: keyof typeof IProduct.MerchandisingType | undefined;
}
